import { FC, useEffect, useState } from "react";
import { toast } from "react-toastify";
import ModalForm from "../../../../components/ModalForm";
import Stat from "../../../../components/Text/Stat";
import { validateWeek } from "../../../../services/api/validatedWeek";
import { Mindset } from "../../../../types/enums/Mindset";
import { Timeslots } from "../../../../types/enums/Timeslots";
import {
    FieldErrors,
    REQUIRED_FIELD,
    clearFieldError,
    notifyFieldErrors,
    setFieldErrors,
} from "../../../../utils/errors";
import { IMAGE_FILE_ACCEPT } from "../../../../utils/fileAccept";
import { getOptionsFromEnum } from "../../../../utils/form";
import styles from "./styles.module.scss";

const TIMESLOT_ORDER = Object.keys(Timeslots) as (keyof typeof Timeslots)[];
const ALLOWED_IMAGE_TYPES = new Set(["image/jpeg", "image/png"]);

function getTimeslotLabel(timeslot: string): string {
    return Timeslots[timeslot as keyof typeof Timeslots] ?? timeslot;
}

function sortBreakdownByTimeslotOrder(
    items: { timeslot: string; total: number }[]
): { timeslot: string; total: number }[] {
    return [...items].sort((a, b) => {
        const i = TIMESLOT_ORDER.indexOf(a.timeslot as keyof typeof Timeslots);
        const j = TIMESLOT_ORDER.indexOf(b.timeslot as keyof typeof Timeslots);
        if (i === -1 && j === -1) return a.timeslot.localeCompare(b.timeslot);
        if (i === -1) return 1;
        if (j === -1) return -1;
        return i - j;
    });
}

export type ValidWeekFormModalRecap = {
    weekResult?: string;
    breakdownByTimeslot?: { timeslot: string; total: number }[];
};

type Props = {
    open: boolean;
    onClose: () => void;
    weekNumber?: number;
    year?: number;
    /** Récap informatif affiché au-dessus du formulaire (aucun post) */
    weekRecap?: ValidWeekFormModalRecap;
};

const ValidWeekFormModal: FC<Props> = ({ open, onClose, weekNumber, year, weekRecap }) => {
    const [mindset, setMindset] = useState<Mindset | null>(null);
    const [file, setFile] = useState<File | null>(null);
    const [comment, setComment] = useState<string>("");
    const [fieldErrors, setFieldErrorsState] = useState<FieldErrors>({});

    const handleSubmit = async () => {
        const nextErrors = setFieldErrors(
            ["mindset", mindset === null && REQUIRED_FIELD],
            ["validWeekFile", file === null && REQUIRED_FIELD]
        );
        setFieldErrorsState(nextErrors);
        const toastMessage = nextErrors.mindset
            ? "Veuillez sélectionner un état d'esprit"
            : nextErrors.validWeekFile
              ? "Veuillez choisir un document"
              : undefined;
        if (
            notifyFieldErrors(nextErrors, {
                message: toastMessage,
                type: "warning",
            })
        ) {
            return;
        }
        if (mindset === null || file === null) return;

        try {
            await validateWeek(weekNumber!, year!, mindset, file, comment || null);
            toast.success("Semaine validée avec succès");
            onClose();
        } catch (error) {
            return;
        }
    };

    const handleFileChange = (selectedFile: File) => {
        if (!ALLOWED_IMAGE_TYPES.has(selectedFile.type)) {
            setFile(null);
            setFieldErrorsState((prev) => ({
                ...prev,
                validWeekFile:
                    "Le fichier doit être une image au format JPG, JPEG ou PNG.",
            }));
            return;
        }
        setFile(selectedFile);
        setFieldErrorsState((prev) => clearFieldError(prev, "validWeekFile"));
    };

    // Si on ferme la modal, on remet les valeurs des inputs à leur valeur initiale
    useEffect(() => {
        if (!open) {
            setMindset(null);
            setFile(null);
            setComment("");
            setFieldErrorsState({});
        }
    }, [open]);

    const hasRecap = weekRecap && (weekRecap.weekResult != null || (weekRecap.breakdownByTimeslot?.length ?? 0) > 0);

    return (
        <ModalForm
            size="small"
            title="Valider la semaine"
            open={open}
            handleClose={onClose}
            onSubmit={handleSubmit}
            contentAbove={
                hasRecap ? (
                    <div className={styles.weekRecap}>
                        <div className={styles.weekRecapRowTitle}>
                            <span className={styles.weekRecapTitle}>Résultats de la semaine</span>
                            <span className={styles.weekRecapTotal}>
                                <Stat variant="body" type="currency" className={styles.weekRecapTotalValue}>
                                    {weekRecap.weekResult ?? 0}
                                </Stat>
                            </span>
                        </div>
                        {weekRecap.breakdownByTimeslot && weekRecap.breakdownByTimeslot.length > 0 && (
                            <ul className={styles.weekRecapList}>
                                {sortBreakdownByTimeslotOrder(weekRecap.breakdownByTimeslot).map(({ timeslot, total }) => (
                                    <li key={timeslot} className={styles.weekRecapRow}>
                                        <span>{getTimeslotLabel(timeslot)}</span>
                                        <Stat variant="body" type="currency">{total}</Stat>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                ) : null
            }
            fields={[
                {
                    id: "mindset",
                    label: "État d'esprit *",
                    type: "select",
                    options: getOptionsFromEnum(Mindset),
                    value: mindset === null ? undefined : mindset,
                    onChange: (value) => {
                        setMindset(value as Mindset);
                        setFieldErrorsState((prev) =>
                            clearFieldError(prev, "mindset")
                        );
                    },
                    error: fieldErrors.mindset,
                },
                {
                    id: "validWeekFile",
                    label: "Historique du compte *",
                    type: "file",
                    accept: IMAGE_FILE_ACCEPT,
                    value: file === null ? undefined : file,
                    onChange: (value) => {
                        if (value) handleFileChange(value);
                    },
                    error: fieldErrors.validWeekFile,
                },
                {
                    id: "comment",
                    label: "Commentaire",
                    type: "textarea",
                    value: comment,
                    onChange: (value) => setComment(value ?? ""),
                    max: 2500,
                },
            ]}
        />
    );
};

export default ValidWeekFormModal;
